import { useEffect, useState } from "react";
import { Save, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { parseAmount, toInputAmount } from "../lib/format";

const toForm = (tx = {}) => ({
  value_date: tx.value_date || "",
  booking_date: tx.booking_date || "",
  amount: toInputAmount(tx.amount),
  transaction_type: tx.transaction_type || "",
  counterparty_raw: tx.counterparty_raw || "",
  booking_text: tx.booking_text || "",
  reference: tx.reference || "",
  position: tx.position ?? "",
  page: tx.page ?? "",
  card_label: tx.card?.label || "",
  card_date: tx.card?.date || "",
  card_time: tx.card?.time || "",
  fx_amount: toInputAmount(tx.fx?.original_amount),
  fx_currency: tx.fx?.original_currency || "",
  fx_country: tx.fx?.merchant_country || "",
  fx_rate: tx.fx?.exchange_rate ?? "",
  fx_fees: toInputAmount(tx.fx?.fees),
  fx_rate_date: tx.fx?.rate_date || "",
});

const nul = (v) => (v === "" || v === undefined ? null : v);

export const fromForm = (f) => {
  const hasCard = f.card_label || f.card_date || f.card_time;
  const hasFx = f.fx_amount || f.fx_currency || f.fx_rate || f.fx_fees;
  return {
    value_date: nul(f.value_date),
    booking_date: nul(f.booking_date) || nul(f.value_date),
    amount: parseAmount(f.amount) ?? 0,
    transaction_type: nul(f.transaction_type),
    counterparty_raw: f.counterparty_raw,
    booking_text: f.booking_text,
    reference: nul(f.reference),
    ...(f.position !== "" && { position: parseFloat(String(f.position).replace(",", ".")) }),
    page: f.page === "" ? null : parseInt(f.page, 10),
    card: hasCard ? { label: nul(f.card_label), date: nul(f.card_date), time: nul(f.card_time) } : null,
    fx: hasFx
      ? {
          original_amount: parseAmount(f.fx_amount),
          original_currency: nul(f.fx_currency),
          merchant_country: nul(f.fx_country),
          exchange_rate: f.fx_rate === "" ? null : parseFloat(String(f.fx_rate).replace(",", ".")),
          fees: parseAmount(f.fx_fees),
          rate_date: nul(f.fx_rate_date),
        }
      : null,
  };
};

const F = ({ id, label, f, set, type = "text", mono, className = "" }) => (
  <div className={`grid gap-1.5 ${className}`}>
    <Label htmlFor={`tx-${id}`} className="text-xs font-semibold text-slate-600">{label}</Label>
    <Input id={`tx-${id}`} data-testid={`tx-field-${id}`} type={type} value={f[id]} onChange={(e) => set({ ...f, [id]: e.target.value })} className={`h-10 bg-white ${mono ? "font-num" : ""}`} />
  </div>
);

export const TransactionForm = ({ tx, onSubmit, submitLabel = "Änderungen speichern", testId = "transaction-form" }) => {
  const [f, set] = useState(toForm(tx));
  const [busy, setBusy] = useState(false);
  useEffect(() => set(toForm(tx)), [tx?.id, tx?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(fromForm(f)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="grid gap-4" data-testid={testId}>
      <div className="grid grid-cols-2 gap-3">
        <F id="value_date" label="Valuta" type="date" f={f} set={set} mono />
        <F id="booking_date" label="Buchungsdatum" type="date" f={f} set={set} mono />
        <F id="amount" label="Betrag in EUR (Belastung mit −)" f={f} set={set} mono className="col-span-2" />
        <F id="counterparty_raw" label="Empfänger / Händler (roh)" f={f} set={set} className="col-span-2" />
        <F id="transaction_type" label="Art" f={f} set={set} />
        <F id="reference" label="Referenz" f={f} set={set} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="tx-booking_text" className="text-xs font-semibold text-slate-600">Buchungstext (vollständig)</Label>
        <Textarea id="tx-booking_text" data-testid="tx-field-booking_text" rows={4} value={f.booking_text} onChange={(e) => set({ ...f, booking_text: e.target.value })} className="font-num bg-white text-sm" />
      </div>
      <fieldset className="grid grid-cols-3 gap-3 rounded-lg border border-slate-200 p-3">
        <legend className="px-1 text-xs font-semibold text-slate-500">Karte</legend>
        <F id="card_label" label="Karte" f={f} set={set} />
        <F id="card_date" label="Kartendatum" type="date" f={f} set={set} mono />
        <F id="card_time" label="Uhrzeit" f={f} set={set} mono />
      </fieldset>
      <fieldset className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 p-3">
        <legend className="px-1 text-xs font-semibold text-slate-500">Fremdwährung / Spesen</legend>
        <F id="fx_amount" label="Originalbetrag" f={f} set={set} mono />
        <F id="fx_currency" label="Währung" f={f} set={set} />
        <F id="fx_rate" label="Kurs" f={f} set={set} mono />
        <F id="fx_fees" label="Spesen (EUR)" f={f} set={set} mono />
        <F id="fx_country" label="Land" f={f} set={set} />
        <F id="fx_rate_date" label="Kursdatum" type="date" f={f} set={set} mono />
      </fieldset>
      <div className="grid grid-cols-2 gap-3">
        <F id="position" label="Position (Reihenfolge)" f={f} set={set} mono />
        <F id="page" label="Seite" f={f} set={set} mono />
      </div>
      <Button type="submit" disabled={busy} data-testid={`${testId}-submit`} className="h-11 gap-2 bg-slate-900 font-semibold hover:bg-slate-800">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {submitLabel}
      </Button>
    </form>
  );
};
